import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon, Search } from 'lucide-react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';
import { useDebounce } from '@/lib/hooks/useDebounce';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { getLocalizedName } from '@/lib/utils';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { User, UserFilters, UserStatus } from '../types';

interface UsersDataTableProps {
  data: User[];
  columns: ColumnDef<User>[];
  isLoading: boolean;
  filters: UserFilters;
  updateFilter: <K extends keyof UserFilters>(key: K, value: UserFilters[K]) => void;
  updateFilters?: (updates: Partial<UserFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

const USER_STATUSES: UserStatus[] = ['ACTIVE', 'BLOCKED'];

const USER_STATUS_ACTIVE: Record<UserStatus, string> = {
  ACTIVE: 'data-[state=on]:bg-emerald-500',
  BLOCKED: 'data-[state=on]:bg-red-500',
};

export function UsersDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: UsersDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('users');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const hasAccess = useAuthStore((state) => state.hasAccess);

  // Debounced search
  const [searchValue, setSearchValue] = useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    // Only update if value actually changed to avoid resetting page
    if (newValue !== filters.s) {
      updateFilter('s', newValue);
    }
  }, [debouncedSearch]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
    status,
  } = filters;

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.s) count++;
    if (filters['createdAt.min'] || filters['createdAt.max']) count++;
    if (filters.roleId) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Toolbar: status pills + table controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-2">
        <StatusFilterPills
          value={status}
          onChange={(v) => updateFilter('status', v as UserStatus | undefined)}
          options={USER_STATUSES.map((s) => ({
            value: s,
            label: t(`status.${s}`),
            activeClassName: USER_STATUS_ACTIVE[s],
          }))}
        />
        <div className="flex items-center gap-2">
          {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={3}
      >
        {/* Search */}
        <FilterField label={t('search')}>
          <div className="relative">
            <Input
              placeholder={t('search')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            />
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </FilterField>

        {/* Created Date Range */}
        <FilterField label={t('createdDateRange')}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
              >
                <span className="truncate">
                  {minDate && maxDate
                    ? `${format(new Date(minDate), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(maxDate), 'MM/dd/yy', { locale: dateFnsLocale })}`
                    : t('all')}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="range"
                selected={{
                  from: minDate ? new Date(minDate) : undefined,
                  to: maxDate ? new Date(maxDate) : undefined,
                }}
                onSelect={(range) => {
                  if (updateFilters) {
                    updateFilters({
                      'createdAt.min': range?.from?.toISOString(),
                      'createdAt.max': range?.to?.toISOString(),
                    });
                  } else {
                    updateFilter('createdAt.min', range?.from?.toISOString());
                    updateFilter('createdAt.max', range?.to?.toISOString());
                  }
                }}
                numberOfMonths={2}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </FilterField>

        {/* Role */}
        {hasAccess('role', 'get') && (
          <FilterField label={t('role')}>
            <UniversalCombobox
              url="/roles"
              listUrl="/roles/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.roleId}
              onSelectItem={(role) =>
                updateFilter('roleId', role ? (role['id'] as number) : undefined)
              }
              renderLabel={(item) => getLocalizedName(item as { name: string; name_en?: string; name_ru?: string }, i18n.language)}
              placeholder={t('all')}
              className="w-full"
            />
          </FilterField>
        )}

      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noUsersFound')}</h3>
                    <p className="text-[#64748b]">{t('addUsersMessage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
